/**
 * EU-588 item 2 (spec 009 do core, v2 LAST-TOUCH) — origem de tráfego do
 * candidato, enviada ao backend para virar `traffic_*` em TODA conversão RD.
 *
 * Fontes (nesta ordem de leitura; o backend decide a precedência RN-01/RN-02):
 *   - cookie `__trf.src` do RD Station (criado pelo loader EU-506 em
 *     app.eureca.me; ele mesmo se atualiza a cada sessão/campanha);
 *   - cookies first-party `eu_utm_*` em `.eureca.me` (EU-587, 90 dias,
 *     re-gravados a cada visita com UTM) com fallback no first-touch do
 *     localStorage (`eureca_mixpanel_first_touch`) e por fim a URL atual.
 *
 * Envio 1×/sessão POR USUÁRIO no boot autenticado, fire-and-forget, SEMPRE
 * com o dado atual do browser — sem flag durável (EU-588 v2, decisão
 * 2026-08-07): o upsert do core é last-touch (o snapshot da sessão corrente
 * substitui o anterior), então reenviar é exatamente o objetivo — cada
 * conversão sai com a origem vigente no momento em que acontece.
 *
 * Nunca lança: cookies/storage bloqueados ou rede fora degradam em silêncio.
 */
import fetchClient from '@eureca/candidate-api-sdk/client';

const SESSION_ATTEMPT_KEY = 'eureca_traffic_origin_attempted';
const FIRST_TOUCH_KEY = 'eureca_mixpanel_first_touch';

export type TrafficOriginPayload = Partial<{
  trfSrc: string;
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
  utmTerm: string;
}>;

function readCookie(name: string): string | undefined {
  try {
    const prefix = `${name}=`;
    const hit = document.cookie.split('; ').find((c) => c.startsWith(prefix));
    if (!hit) return undefined;
    const raw = hit.slice(prefix.length);
    try {
      return decodeURIComponent(raw) || undefined;
    } catch {
      return raw || undefined;
    }
  } catch {
    return undefined;
  }
}

function readFirstTouchUtm(): Partial<Record<string, string>> {
  try {
    const raw = localStorage.getItem(FIRST_TOUCH_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as { utm?: Partial<Record<string, string>> };
    return parsed.utm ?? {};
  } catch {
    return {};
  }
}

/** Coleta a origem disponível no browser (objeto vazio quando não há nada). */
export function collectTrafficOrigin(): TrafficOriginPayload {
  const payload: TrafficOriginPayload = {};
  try {
    const trfSrc = readCookie('__trf.src');
    if (trfSrc) payload.trfSrc = trfSrc;

    const ft = readFirstTouchUtm();
    // EU-588 hardening — a URL atual é o último fallback: cobre o caso de o
    // envio rodar antes de qualquer evento de analytics ter gravado
    // cookie/first-touch (ex.: boot autenticado numa URL que ainda carrega UTM).
    let urlParams: URLSearchParams | null = null;
    try {
      urlParams = new URLSearchParams(window.location.search);
    } catch {
      /* sem URL */
    }
    const utm = (key: string) =>
      readCookie(`eu_${key}`) ?? ft[key] ?? urlParams?.get(key) ?? undefined;

    const utmSource = utm('utm_source');
    const utmMedium = utm('utm_medium');
    const utmCampaign = utm('utm_campaign');
    const utmTerm = utm('utm_term');
    if (utmSource) payload.utmSource = utmSource;
    if (utmMedium) payload.utmMedium = utmMedium;
    if (utmCampaign) payload.utmCampaign = utmCampaign;
    if (utmTerm) payload.utmTerm = utmTerm;
  } catch {
    /* cookies/storage bloqueados — devolve o que montou */
  }
  return payload;
}

/**
 * EU-588 QA — a flag de sessão é escopada pelo `sub` do usuário autenticado:
 * a versão global (browser-wide) silenciava o envio de OUTRO usuário que
 * logasse na mesma aba (logout→login), e o lead novo nascia sem origem no RD
 * ("Origem: Desconhecido"). As flags DURÁVEIS legadas (`…_sent`, com ou sem
 * sub) são deliberadamente ignoradas — no last-touch não existe "já enviado
 * de vez".
 */
function attemptKey(userSub?: string): string {
  return userSub ? `${SESSION_ATTEMPT_KEY}:${userSub}` : SESSION_ATTEMPT_KEY;
}

/**
 * Envia a origem ATUAL do browser ao BFF uma vez por sessão POR USUÁRIO —
 * toda sessão, sempre (last-touch: o core sobrescreve o snapshot anterior).
 * A resposta é ignorada; falha fica pro reenvio natural da próxima sessão.
 * Chamar apenas com o candidato AUTENTICADO (o BFF exige bearer), passando
 * o `sub` do token.
 */
export async function maybeSendTrafficOrigin(userSub?: string): Promise<void> {
  try {
    if (typeof window === 'undefined') return;
    const key = attemptKey(userSub);
    if (sessionStorage.getItem(key) === '1') return;

    const payload = collectTrafficOrigin();
    if (Object.keys(payload).length === 0) return; // nada capturado ainda — tenta na próxima

    sessionStorage.setItem(key, '1');

    await fetchClient({
      method: 'POST',
      url: '/me/traffic-origin',
      data: payload,
    });
  } catch (err) {
    console.warn('[eureca-traffic-origin] send failed (non-fatal)', err);
  }
}
